import React, { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import cn from 'classnames'
import { Share2, UserPlus } from 'lucide-react'
import api, { BsvTdpStatsResponse, ShareRewardsLeaderboardResponse } from 'api/api'
import { extractTdpEmbedConfig, TdpEmbedConfig } from 'components/shared/LinkPreview'
import { captureReportShareClick } from 'components/shared/captureReportShareClick'
import ShareToEarnLabel from 'components/shared/ShareToEarnLabel'
import useStore from 'state/knovStore'
import { TdpSentimentCard, TdpShareRewardsCard, TdpValueCard } from './TdpCards'
import { DAILY_SHARE_REWARDS_SHARER_LIMIT } from './reportConfig'
import styles from './tdp-message-embed.module.scss'

interface TdpMessageEmbedProps {
    className?: string
    config?: TdpEmbedConfig
    url?: string
    onContentLayoutSettled?: () => void
    onShare?: () => void
    onJoin?: () => void
}

const addUtcDays = (ymd: string, days: number) => {
    const [year, month, day] = ymd.split('-').map(Number)
    const date = new Date(Date.UTC(year, month - 1, day))
    date.setUTCDate(date.getUTCDate() + days)
    const y = date.getUTCFullYear()
    const m = String(date.getUTCMonth() + 1).padStart(2, '0')
    const d = String(date.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

const formatUtcDateLabel = (ymd: string) => {
    const [year, month, day] = ymd.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    })
}

export default function TdpMessageEmbed({
    className,
    config: embedConfig,
    onContentLayoutSettled,
    onJoin,
    onShare,
    url,
}: TdpMessageEmbedProps) {
    const config = useMemo(
        () => embedConfig || (url ? extractTdpEmbedConfig(url) : null),
        [embedConfig, url],
    )
    const openTdpViewerModal = useStore(state => state.actions.modalActions.openTdpViewerModal)
    const openLeaderboardModal = useStore(state => state.actions.modalActions.openLeaderboardModal)

    const isShareRewards = config?.view === 'share_rewards'

    const tdpQuery = useQuery<BsvTdpStatsResponse | null>({
        queryKey: ['tdp', 'messageEmbed', config?.view, config?.date],
        queryFn: () =>
            api.getBsvTdpStats({
                start_date: config?.date,
                end_date: config?.date ? addUtcDays(config.date, 1) : undefined,
            }),
        enabled: !!config && !isShareRewards,
        staleTime: 5 * 60_000,
    })

    const shareRewardsQuery = useQuery<ShareRewardsLeaderboardResponse>({
        queryKey: ['shareRewardsLeaderboard', 'messageEmbed', config?.date],
        queryFn: () =>
            api.getShareRewardsLeaderboard({
                start_date: config?.date,
                end_date: config?.date ? addUtcDays(config.date, 1) : undefined,
                page: 1,
                per_page: DAILY_SHARE_REWARDS_SHARER_LIMIT,
            }),
        enabled: !!config && isShareRewards,
        staleTime: 5 * 60_000,
    })

    const query = isShareRewards ? shareRewardsQuery : tdpQuery
    const data = tdpQuery.data
    const breakdown = data?.breakdown?.range
    const rangeAddedBsv = data?.range_added_bsv ?? 0
    const upvaluesBsv = (breakdown?.upvalues_sats || 0) / 100_000_000
    const aiFeesBsv = (breakdown?.ai_fee_sats || 0) / 100_000_000
    const ordinalSalesBsv = (breakdown?.ordinal_sales_sats || 0) / 100_000_000
    const topReactionUpvalues = data?.top_reaction_upvalues ?? []
    const shareTotals = shareRewardsQuery.data?.totals
    const topSharers = shareRewardsQuery.data?.users ?? []
    const queryFailed = !!query.error || (!isShareRewards && !!data?.error)
    const settledLayoutKey = [
        config?.view || 'none',
        query.isLoading ? 'loading' : queryFailed ? 'error' : 'ready',
        topReactionUpvalues.length,
        rangeAddedBsv,
        topSharers.length,
        shareTotals?.total_sats ?? 0,
    ].join(':')

    useEffect(() => {
        if (!config || query.isLoading) return
        onContentLayoutSettled?.()
    }, [config, onContentLayoutSettled, query.isLoading, settledLayoutKey])

    if (!config) return null

    const dateLabel = formatUtcDateLabel(config.date)
    const stateTitle =
        config.view === 'tdp'
            ? `${dateLabel} Treechat Economy 1D`
            : config.view === 'sentiment'
            ? `${dateLabel} Sentiment`
            : `${dateLabel} Share Rewards Earned`
    const footer = (
        <button
            type="button"
            className={styles.footer}
            onClick={() =>
                isShareRewards ? openLeaderboardModal('sharers', config.date) : openTdpViewerModal()
            }
        >
            {isShareRewards ? 'View Leaderboards' : 'View TDP'}
            <i className="fa fa-chevron-right" />
        </button>
    )
    const content = query.isLoading ? (
        <StateContent title={stateTitle}>Loading…</StateContent>
    ) : queryFailed ? (
        <StateContent title={stateTitle}>
            {isShareRewards ? 'Failed to load share rewards' : 'Failed to load TDP'}
        </StateContent>
    ) : config.view === 'tdp' ? (
        <TdpValueCard
            aiFeesBsv={aiFeesBsv}
            heading="TREECHAT ECONOMY 1D"
            headingSubtext={dateLabel}
            ordinalSalesBsv={ordinalSalesBsv}
            rangeAddedBsv={rangeAddedBsv}
            upvaluesBsv={upvaluesBsv}
            variant="embed"
        />
    ) : config.view === 'sentiment' ? (
        <TdpSentimentCard
            emptyLabel="No sentiment data yet"
            heading="TREECHAT SENTIMENT"
            headingSubtext={dateLabel}
            reactions={topReactionUpvalues}
            variant="embed"
        />
    ) : (
        <TdpShareRewardsCard
            emptyLabel="No share rewards sent this day"
            heading="SHARE REWARDS EARNED"
            headingSubtext={dateLabel}
            shareCount={shareTotals?.share_count ?? 0}
            sharerCount={shareTotals?.sharer_count ?? 0}
            sharers={topSharers.slice(0, DAILY_SHARE_REWARDS_SHARER_LIMIT)}
            totalSats={shareTotals?.total_sats ?? 0}
            variant="embed"
        />
    )

    return (
        <div className={cn(styles.container, className)}>
            <div className={styles.embedFrame}>
                {content}
                {onShare ? (
                    <button
                        type="button"
                        className={styles.shareWide}
                        onClick={event => captureReportShareClick(event, onShare)}
                        data-testid="report-embed-share-button"
                    >
                        <Share2 size={15} aria-hidden />
                        <ShareToEarnLabel fallback="Share this report" />
                    </button>
                ) : onJoin ? (
                    <button
                        type="button"
                        className={styles.shareWide}
                        onClick={onJoin}
                        data-testid="report-embed-join-button"
                    >
                        <UserPlus size={15} aria-hidden />
                        <span>Join Treechat</span>
                    </button>
                ) : null}
                {footer}
            </div>
        </div>
    )
}

function StateContent({ children, title }: { children: React.ReactNode; title: React.ReactNode }) {
    return (
        <div className={styles.stateContent}>
            <div className={styles.stateTitle}>{title}</div>
            <div className={styles.stateRow}>{children}</div>
        </div>
    )
}
